import { useState } from "react";
import { Alert, Badge, Button, Card, Grid, Group, Stack, Text, Title } from "@mantine/core";
import { IconChefHat } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { KitchenToolRequest } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { AiContextFields } from "../components/AiContextFields";
import { MediaInput, type MediaInputValue } from "../components/MediaInput";
import { RecipeView } from "../components/RecipeView";
import { ExampleList, KitchenPending, PreferencesFields } from "../components/kitchen";
import { composeMessage } from "../lib/clientSettings";
import { alternateRequest, buildPreferences, kitchenRequest, type PreferencesForm } from "../lib/kitchen";

const EXAMPLES = ["Chicken tikka masala from my favourite Indian place", "The crispy smashed double cheeseburger from a burger stand", "Pad see ew with beef, but gluten-free"];

export function RecreateDishPage() {
  const [input, setInput] = useState<MediaInputValue>({ text: "", media: null });
  const [prefs, setPrefs] = useState<PreferencesForm>({ servings: 2 });
  const [note, setNote] = useState("");
  const [last, setLast] = useState<KitchenToolRequest | null>(null);
  const [pendingAlt, setPendingAlt] = useState<string | null>(null);
  const m = useMutation({ mutationFn: (req: KitchenToolRequest) => passio.recipes.recreateDish(req), onSettled: () => setPendingAlt(null) });

  const send = (req: KitchenToolRequest) => {
    setLast(req);
    m.mutate(req);
  };
  const submit = (text = input.text) => {
    if (!text.trim() && !input.media) return;
    setInput({ ...input, text });
    send(kitchenRequest({ text, media: input.media, message: composeMessage(note), preferences: buildPreferences(prefs) }));
  };
  const makeAlternate = (title: string) => {
    if (!last) return;
    setPendingAlt(title);
    send(alternateRequest(last, title));
  };
  const data = m.data?.data;
  const firstLoad = m.isPending && !pendingAlt;

  return (
    <RequireFeature feature="cooking">
      <PageHeader
        eyebrow="POST /napi/tools/recipes/recreateDish"
        title="Recreate a dish"
        description="A restaurant plate, a video from a food stall, or just a name: the dish is identified and turned into a home recipe with linked ingredients, adapted to the user's diet and allergies."
        actions={<RouteModelPicker routes={["recipesRecreateDish"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <MediaInput value={input} onChange={setInput} textPlaceholder="Name or describe the dish…" photoLabel="Drop a photo of the dish" />
              <PreferencesFields value={prefs} onChange={setPrefs} fields={["diet", "allergies", "dislikes", "servings", "maxMinutes", "skill", "equipment"]} />
              <AiContextFields note={note} onNoteChange={setNote} placeholder="e.g. make it less spicy" />
              <Button leftSection={<IconChefHat size={16} />} onClick={() => submit()} loading={firstLoad} disabled={m.isPending || (!input.text.trim() && !input.media)} size="md">
                Get the recipe
              </Button>
              {!input.media && <ExampleList examples={EXAMPLES} onPick={submit} />}
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {m.error && <ErrorNotice error={m.error} onRetry={() => last && send(last)} />}
          {firstLoad && <KitchenPending title="Working out the dish…" />}
          {data && !firstLoad && (
            <Stack gap="md">
              <ResultHeader title="Recreated" meta={m.data?.meta} feedback={<FeedbackButtons requestId={m.data?.meta.requestId} />} />
              {data.dishName && (
                <Card>
                  <Group gap="xs">
                    <Title order={3} fz={20}>
                      {data.dishName}
                    </Title>
                    <Badge variant="light" color={data.confidence >= 0.75 ? "lime.8" : data.confidence >= 0.4 ? "orange" : "gray"}>
                      {Math.round(data.confidence * 100)}% sure
                    </Badge>
                  </Group>
                  {data.identifiedFrom && (
                    <Text fz="sm" c="dimmed" mt={4}>
                      Identified from: {data.identifiedFrom}
                    </Text>
                  )}
                </Card>
              )}
              {data.notes && (
                <Alert color="violet" variant="light">
                  <Text fz="sm">{data.notes}</Text>
                </Alert>
              )}
              {data.recipe ? (
                <RecipeView recipe={data.recipe} alternates={data.similarDishes} alternatesTitle="Similar dishes" onAlternate={makeAlternate} pendingAlternate={pendingAlt} cartTitle={data.dishName} />
              ) : (
                <EmptyState title="That doesn't look like a dish">{data.notes || "Try a closer photo of the plate, or type the dish name."}</EmptyState>
              )}
            </Stack>
          )}
          {!data && !m.isPending && !m.error && <EmptyState title="Seen something good?">Photos of the plate from above, or a few seconds of video, work well. A name works too.</EmptyState>}
        </Grid.Col>
      </Grid>
    </RequireFeature>
  );
}
